import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialToDoState } from 'store/initial-states';
import {
  convertBrianDumpToTasks,
  fetchCalendarData,
  getToDos,
  searchToDos,
  fetchTaskComments,
  fetchTaskReactions,
  createTaskComment,
  updateTaskComment,
  deleteTaskComment,
  addCommentReaction,
  removeCommentReaction,
  addTaskReaction,
  removeTaskReaction,
  fetchTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
  fetchCommentAttachments,
  uploadCommentAttachment,
  deleteCommentAttachment,
  persistToDoTask,
  submitToDoFromFlow,
  deleteToDo
} from './extra';
import {
  BrainDumpTask,
  CommentReaction,
  ToDoTask
} from 'interfaces/common.interface';
import {
  DEFAULT_TO_DO_PLAYER,
  INTEGRATION_PLATFORM,
  PAGE
} from 'constants/general';
import { ToDoPlayerTask } from 'interfaces';
import { v4 as uuid } from 'uuid';
import { formatToDoPlayerTaskTimeConstraints } from 'utils/helpers';
import { filterValidPlayerTasks } from 'utils/todo-player.util';
import { TO_DO_STATUS } from 'enums';
import { FilterDataProps, InitFocusPlayerPayload } from 'interfaces/todos.interface';
import { DEFAULT_FILTER_DATA } from 'assets/data';
import {
  filterNewTasks,
  rebuildMergedTasksFromBuckets,
  removeTaskAcrossBuckets,
  sortTasksBySortMode
} from 'utils/todo.util';
import {
  applyMergedTodoTaskFromPayload,
  isValidTodoTaskMergePayload
} from 'utils/todo-task-sync.util';

const todo = createSlice({
  name: 'to-do',
  initialState: initialToDoState,
  reducers: {
    updateTasks: (state, { payload }) => {
      state.tasks = payload;
    },
    updateIsCreatingToDo: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingToDo = payload;
    },
    updateIsFetchingToDo: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingToDo = payload;
    },
    updateIsUpdatingToDo: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingToDo = payload;
    },
    updateShouldResetNewTaskForm: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.shouldResetNewTaskForm = payload;
    },
    updateShouldUpdateTextEditorContent: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.shouldUpdateTextEditorContent = payload;
    },
    updateUpdatingTaskId: (
      state,
      { payload }: PayloadAction<{ id: string; isUpdating: boolean }>
    ) => {
      if (payload.isUpdating) {
        state.updatingTaskIds[payload.id] = true;
      } else {
        const { [payload.id]: _removed, ...remaining } = state.updatingTaskIds;
        state.updatingTaskIds = remaining;
      }
      state.isTaskUpdating = Object.keys(state.updatingTaskIds).length > 0;
    },
    updateTaskInTodoList: (state, { payload }: PayloadAction<ToDoTask>) => {
      applyMergedTodoTaskFromPayload(state, payload);
    },
    updateShowEmbeddedCreateToDoModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showEmbeddedCreateToDoModal = payload;
    },
    updateIsTaskSubmitting: (state, { payload }: PayloadAction<boolean>) => {
      state.isTaskSubmitting = payload;
    },
    updateToDoActiveTabIndex: (state, { payload }: PayloadAction<number>) => {
      state.activeTabIndex = payload;
    },
    updateNewTask: (state, { payload }: PayloadAction<ToDoTask>) => {
      state.newTask = payload;
    },
    updateNewTodoPlayerTask: (
      state,
      { payload }: PayloadAction<ToDoTask | null>
    ) => {
      state.newTodoPlayerTask = payload;
    },
    updateIncomingTasks: (state, { payload }) => {
      state.incomingTasks = payload;
    },
    initFocusPlayer: (
      state,
      {
        payload: {
          tasks,
          intention,
          total_duration,
          brainDump,
          focusModeEndTime,
          classSessionId
        }
      }: PayloadAction<InitFocusPlayerPayload>
    ) => {
      state.player.tasks = tasks;
      state.player.intention = intention;
      state.player.total_duration = total_duration;
      state.player.brainDump = brainDump;
      state.player.focusModeEndTime = focusModeEndTime;
      state.player.focusModePauseStartedAtMs = null;
      state.player.focusModeAccumulatedPauseOffsetMs = 0;
      if (classSessionId !== undefined) {
        state.player.classSessionId = classSessionId;
      }
      state.player.isPlayerMetaResolved = true;
    },
    updatePlayerClassSessionId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.player.classSessionId = payload;
    },
    updatePlayerMetaResolved: (state, { payload }: PayloadAction<boolean>) => {
      state.player.isPlayerMetaResolved = payload;
    },
    hydratePlayerClassSessionId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.player.classSessionId = payload;
    },
    updateFocusPlayerTotalDuration: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.player.total_duration = payload;
    },
    updatePlayerTasks: (
      state,
      { payload }: PayloadAction<ToDoPlayerTask[] | undefined>
    ) => {
      state.player.tasks = filterValidPlayerTasks(payload);
    },
    updateIsSearchingToDos: (state, { payload }: PayloadAction<boolean>) => {
      state.player.isSearchingToDos = payload;
    },
    updateTasksPlayedIds: (state, { payload }: PayloadAction<string[]>) => {
      state.player.tasksPlayedIds = payload;
    },
    updatePlayerCompletedTask: (
      state,
      { payload }: PayloadAction<ToDoPlayerTask | null>
    ) => {
      state.player.completedTask = payload;
      state.player.isSelectedTaskCompleted = Boolean(payload);
    },
    updatePlayerSelectedTask: (
      state,
      { payload }: PayloadAction<ToDoPlayerTask | undefined>
    ) => {
      state.player.selectedTask =
        payload?.status === TO_DO_STATUS.COMPLETED ? undefined : payload;
    },
    updatePlayerTaskCompletion: (
      state,
      {
        payload: {
          isSelectedTaskCompleted,
          completedTask,
          tasks,
          tasksPlayedIds,
          selectedTask
        }
      }: PayloadAction<{
        isSelectedTaskCompleted: boolean;
        completedTask: ToDoPlayerTask | null;
        tasks?: ToDoPlayerTask[];
        tasksPlayedIds?: string[];
        selectedTask?: ToDoPlayerTask;
      }>
    ) => {
      state.player.isSelectedTaskCompleted = isSelectedTaskCompleted;
      state.player.completedTask = completedTask;
      if (tasks) {
        state.player.tasks = tasks;
      }

      if (selectedTask) {
        state.player.selectedTask = selectedTask;
      }
      if (tasksPlayedIds) {
        state.player.tasksPlayedIds = tasksPlayedIds;
      }
    },
    updatePlayerIsReadyToSendUpdates: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      if (!state.player.isSyncingFromDesktop) {
        state.player.isReadyToSendUpdates = payload;
      }
    },
    setPlayerIsSyncingFromDesktop: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.player.isSyncingFromDesktop = payload;
    },
    updateIsConvertingBrianDump: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isConvertingBrianDump = payload;
    },
    updateShowBrainDumpTasksModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showBrainDumpTasksModal = payload;
    },
    updateBrainDumpModalConvertedTasks: (
      state,
      { payload }: PayloadAction<BrainDumpTask[]>
    ) => {
      state.player.brainDumpModalConvertedTasks = payload;
    },
    updateIsAddingConvertedTasksToPlayer: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAddingConvertedTasksToPlayer = payload;
    },
    updatePlayerAddTask: (
      state,
      { payload }: PayloadAction<ToDoPlayerTask>
    ) => {
      state.player.tasks = formatToDoPlayerTaskTimeConstraints([
        ...state.player.tasks,
        payload
      ]);
    },
    updatePlayerSearchResults: (
      state,
      { payload }: PayloadAction<ToDoTask[] | null>
    ) => {
      state.player.searchResults = payload;
    },
    updatePlayerIntention: (state, { payload }: PayloadAction<string>) => {
      state.player.intention = payload;
    },
    updatePlayerFocusAlignmentScore: (
      state,
      { payload }: PayloadAction<number | undefined>
    ) => {
      state.player.focusAlignmentScore = payload;
    },
    resetToDoPlayer: (state) => {
      state.player = DEFAULT_TO_DO_PLAYER;
    },
    updatePlayerShowNewTaskModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.player.showPlayerNewTaskModal = payload;
    },
    updateTopScoreDrawerTaskId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.player.topScoreDrawerTaskId = payload;
    },
    updateBrainDump: (state, { payload }: PayloadAction<string>) => {
      state.player.brainDump = payload;
    },
    syncWithDevicesPlayerTasks: (
      state,
      { payload }: PayloadAction<ToDoPlayerTask[]>
    ) => {
      state.player.tasks = [...state.player.tasks, ...payload];
      if (!state.player.isSyncingFromDesktop) {
        state.player.isReadyToSendUpdates = true;
      }
    },
    updateTodoFilters: (state, { payload }: PayloadAction<FilterDataProps>) => {
      state.todoFilters = {
        ...state.todoFilters,
        ...payload
      };
    },
    resetTodoFilters: (state) => {
      state.todoFilters = DEFAULT_FILTER_DATA;
    },
    resetTodoPanelFilters: (state) => {
      const { taskName, sortMode, order } = state.todoFilters;
      state.todoFilters = {
        ...DEFAULT_FILTER_DATA,
        taskName,
        sortMode,
        order
      };
    },
    updateShowNewTaskForm: (state, { payload }: PayloadAction<boolean>) => {
      state.showNewTaskForm = payload;
    },
    updateSelectedTaskToTaskForm: (
      state,
      { payload }: PayloadAction<ToDoTask | null>
    ) => {
      state.selectedTaskToTaskForm = payload;
    },
    updateIsDeletingTask: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeletingTask = payload;
    },
    updateShowVagueInputPrompt: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showVagueInputPrompt = payload;
    },
    updateTaskInSearchResults: (
      state,
      { payload }: PayloadAction<ToDoTask>
    ) => {
      applyMergedTodoTaskFromPayload(state, payload);
    },
    updateIsPausedFromDesktop: (state, { payload }: PayloadAction<boolean>) => {
      const wasPausedFromDesktop = state.player.isPausedFromDesktop;
      if (payload === wasPausedFromDesktop) {
        return;
      }

      const now = Date.now();

      if (payload) {
        state.player.focusModePauseStartedAtMs = now;
      } else {
        const pauseStartedAt = state.player.focusModePauseStartedAtMs;
        if (pauseStartedAt !== null) {
          state.player.focusModeAccumulatedPauseOffsetMs +=
            now - pauseStartedAt;
        }
        state.player.focusModePauseStartedAtMs = null;
      }

      state.player.isPausedFromDesktop = payload;
    },
    addReactionToComment: (
      state,
      {
        payload
      }: PayloadAction<{
        taskId: string;
        commentId: string;
        reaction: CommentReaction;
      }>
    ) => {
      const comments = state.taskComments[payload.taskId];
      if (!comments) return;
      const comment = comments.find((c) => c.id === payload.commentId);
      if (!comment) return;
      if (!comment.reactions) {
        comment.reactions = [];
      }
      const alreadyExists = comment.reactions.some(
        (r) => r.id === payload.reaction.id
      );
      if (!alreadyExists) {
        comment.reactions.push(payload.reaction);
      }
    },
    removeReactionFromComment: (
      state,
      {
        payload
      }: PayloadAction<{
        taskId: string;
        commentId: string;
        emoji?: string;
        currentUserId?: string;
        reactionId?: string;
      }>
    ) => {
      const comments = state.taskComments[payload.taskId];
      if (!comments) return;
      const comment = comments.find((c) => c.id === payload.commentId);
      if (!comment?.reactions) return;
      if (payload.reactionId != null) {
        comment.reactions = comment.reactions.filter(
          (r) => r.id !== payload.reactionId
        );
      } else if (payload.emoji != null) {
        const userId = payload.currentUserId;
        comment.reactions = comment.reactions.filter(
          (r) =>
            !(r.emoji === payload.emoji && (!userId || r.user_id === userId))
        );
      }
    },
    removeReactionFromTask: (
      state,
      {
        payload
      }: PayloadAction<{
        taskId: string;
        emoji: string;
        currentUserId?: string;
      }>
    ) => {
      const reactions = state.taskReactions[payload.taskId];
      if (!reactions) return;
      const userId = payload.currentUserId;
      state.taskReactions[payload.taskId] = reactions.filter(
        (r) => !(r.emoji === payload.emoji && (!userId || r.user_id === userId))
      );
    },
    updateShowFilters: (state, { payload }: PayloadAction<boolean>) => {
      state.showFilters = payload;
    },
    updateWheelSource: (
      state,
      { payload }: PayloadAction<'player' | 'todoList' | null>
    ) => {
      state.wheelSource = payload;
    },
    updatePlayerSelectedTabIndex: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.player.selectedTabIndex = payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getToDos.fulfilled, (state, { payload }) => {
        if (!payload) {
          state.isFetchingToDo = false;
          return;
        }

        const hideFetch = Boolean(payload.omitFetchIndicator);

        if (payload.status) {
          const list = payload.data ?? [];
          if (payload.replace || !payload.append) {
            state.tasksByStatus[payload.status] = list;
          } else {
            const newTasks = filterNewTasks(
              state.tasksByStatus[payload.status],
              list
            );
            state.tasksByStatus[payload.status].push(...newTasks);
          }
          // The API order is unreliable, so order buckets client-side from the
          // active sort mode (TOP / updated_at / due_date).
          state.tasksByStatus[payload.status] = sortTasksBySortMode(
            state.tasksByStatus[payload.status],
            state.todoFilters.sortMode
          );
          const previousItemCount =
            state.todoStatusPagination[payload.status]?.itemCount ?? 0;
          state.todoStatusPagination[payload.status] = {
            page: payload.requestedPage ?? PAGE.FIRST,
            hasNextPage: Boolean(payload.hasNextPage),
            itemCount:
              typeof payload.itemCount === 'number'
                ? payload.itemCount
                : previousItemCount
          };
          rebuildMergedTasksFromBuckets(state);
        } else {
          if (payload.replace) {
            state.tasks = payload.data ?? [];
          } else {
            const newTasks = filterNewTasks(state.tasks, payload.data ?? []);
            if (payload.append) {
              state.tasks.push(...newTasks);
            } else {
              state.tasks = [...state.incomingTasks, ...newTasks];
            }
          }
        }

        if (!hideFetch) {
          state.isFetchingToDo = false;
          state.showNewTaskForm = false;
        }
      })
      .addCase(fetchCalendarData.fulfilled, (state, { payload }) => {
        if (payload?.platform === INTEGRATION_PLATFORM.GOOGLE) {
          state.googleCalendarData = payload.data ?? {};
        } else {
          state.microsoftCalendarData = payload?.data ?? {};
        }
      })
      .addCase(searchToDos.fulfilled, (state, { payload }) => {
        state.player.searchResults = payload;
        state.player.isSearchingToDos = false;
      })
      .addCase(searchToDos.rejected, (state) => {
        state.player.isSearchingToDos = false;
      })
      .addCase(persistToDoTask.fulfilled, (state, { payload }) => {
        if (!isValidTodoTaskMergePayload(payload)) return;
        applyMergedTodoTaskFromPayload(state, payload);
      })
      // Drop the deleted task from local state right away so the list updates
      // even if the background bucket refresh fails or a search is active.
      .addCase(deleteToDo.fulfilled, (state, { payload }) => {
        if (!payload) return;
        removeTaskAcrossBuckets(state, payload);
        if (state.player.searchResults) {
          state.player.searchResults = state.player.searchResults.filter(
            (task) => task.id !== payload
          );
        }
      })
      .addCase(submitToDoFromFlow.fulfilled, (state, { payload }) => {
        if (!isValidTodoTaskMergePayload(payload)) return;
        applyMergedTodoTaskFromPayload(state, payload);
      })
      .addCase(convertBrianDumpToTasks.fulfilled, (state, { payload }) => {
        state.player.brainDumpModalConvertedTasks = payload?.map(
          (task: BrainDumpTask) => ({
            ...task,
            selected: true,
            id: uuid()
          })
        );
        state.isConvertingBrianDump = false;
      })
      .addCase(fetchTaskComments.pending, (state) => {
        state.isFetchingComments = true;
      })
      .addCase(fetchTaskComments.fulfilled, (state, { payload }) => {
        if (payload) {
          const existingComments = state.taskComments[payload.taskId] ?? [];
          state.taskComments[payload.taskId] = payload.comments.map(
            (comment) => {
              const existing = existingComments.find(
                (c) => c.id === comment.id
              );
              if (existing?.reactions && !comment.reactions) {
                return { ...comment, reactions: existing.reactions };
              }
              return comment;
            }
          );
        }
        state.isFetchingComments = false;
      })
      .addCase(fetchTaskComments.rejected, (state) => {
        state.isFetchingComments = false;
      })
      .addCase(createTaskComment.pending, (state) => {
        state.isCreatingComment = true;
      })
      .addCase(createTaskComment.fulfilled, (state) => {
        state.isCreatingComment = false;
      })
      .addCase(createTaskComment.rejected, (state) => {
        state.isCreatingComment = false;
      })
      .addCase(updateTaskComment.pending, (state) => {
        state.isUpdatingComment = true;
      })
      .addCase(updateTaskComment.fulfilled, (state) => {
        state.isUpdatingComment = false;
      })
      .addCase(updateTaskComment.rejected, (state) => {
        state.isUpdatingComment = false;
      })
      .addCase(deleteTaskComment.pending, (state) => {
        state.isDeletingComment = true;
      })
      .addCase(deleteTaskComment.fulfilled, (state) => {
        state.isDeletingComment = false;
      })
      .addCase(deleteTaskComment.rejected, (state) => {
        state.isDeletingComment = false;
      })
      .addCase(addCommentReaction.pending, (state) => {
        state.isAddingReaction = true;
      })
      .addCase(addCommentReaction.fulfilled, (state) => {
        state.isAddingReaction = false;
      })
      .addCase(addCommentReaction.rejected, (state) => {
        state.isAddingReaction = false;
      })
      .addCase(removeCommentReaction.pending, (state) => {
        state.isRemovingReaction = true;
      })
      .addCase(removeCommentReaction.fulfilled, (state) => {
        state.isRemovingReaction = false;
      })
      .addCase(removeCommentReaction.rejected, (state) => {
        state.isRemovingReaction = false;
      })
      .addCase(fetchTaskReactions.pending, (state) => {
        state.isFetchingTaskReactions = true;
      })
      .addCase(fetchTaskReactions.fulfilled, (state, { payload }) => {
        if (payload) {
          state.taskReactions[payload.taskId] = payload.reactions;
        }
        state.isFetchingTaskReactions = false;
      })
      .addCase(fetchTaskReactions.rejected, (state) => {
        state.isFetchingTaskReactions = false;
      })
      .addCase(addTaskReaction.pending, (state) => {
        state.isAddingTaskReaction = true;
      })
      .addCase(addTaskReaction.fulfilled, (state) => {
        state.isAddingTaskReaction = false;
      })
      .addCase(addTaskReaction.rejected, (state) => {
        state.isAddingTaskReaction = false;
      })
      .addCase(removeTaskReaction.pending, (state) => {
        state.isRemovingTaskReaction = true;
      })
      .addCase(removeTaskReaction.fulfilled, (state) => {
        state.isRemovingTaskReaction = false;
      })
      .addCase(removeTaskReaction.rejected, (state) => {
        state.isRemovingTaskReaction = false;
      })
      .addCase(fetchTaskAttachments.pending, (state) => {
        state.isFetchingAttachments = true;
      })
      .addCase(fetchTaskAttachments.fulfilled, (state, { payload }) => {
        if (payload) {
          state.taskAttachments[payload.taskId] = payload.attachments;
        }
        state.isFetchingAttachments = false;
      })
      .addCase(fetchTaskAttachments.rejected, (state) => {
        state.isFetchingAttachments = false;
      })
      .addCase(uploadTaskAttachment.pending, (state) => {
        state.isUploadingAttachment = true;
      })
      .addCase(uploadTaskAttachment.fulfilled, (state) => {
        state.isUploadingAttachment = false;
      })
      .addCase(uploadTaskAttachment.rejected, (state) => {
        state.isUploadingAttachment = false;
      })
      .addCase(deleteTaskAttachment.pending, (state) => {
        state.isDeletingAttachment = true;
      })
      .addCase(deleteTaskAttachment.fulfilled, (state) => {
        state.isDeletingAttachment = false;
      })
      .addCase(deleteTaskAttachment.rejected, (state) => {
        state.isDeletingAttachment = false;
      })
      .addCase(fetchCommentAttachments.pending, (state) => {
        state.isFetchingCommentAttachments = true;
      })
      .addCase(fetchCommentAttachments.fulfilled, (state, { payload }) => {
        if (payload) {
          state.commentAttachments[payload.commentId] = payload.attachments;
        }
        state.isFetchingCommentAttachments = false;
      })
      .addCase(fetchCommentAttachments.rejected, (state) => {
        state.isFetchingCommentAttachments = false;
      })
      .addCase(uploadCommentAttachment.pending, (state) => {
        state.isUploadingCommentAttachment = true;
      })
      .addCase(uploadCommentAttachment.fulfilled, (state) => {
        state.isUploadingCommentAttachment = false;
      })
      .addCase(uploadCommentAttachment.rejected, (state) => {
        state.isUploadingCommentAttachment = false;
      })
      .addCase(deleteCommentAttachment.pending, (state) => {
        state.isDeletingCommentAttachment = true;
      })
      .addCase(deleteCommentAttachment.fulfilled, (state) => {
        state.isDeletingCommentAttachment = false;
      })
      .addCase(deleteCommentAttachment.rejected, (state) => {
        state.isDeletingCommentAttachment = false;
      });
  }
});

export const {
  updateTasks,
  updateIsCreatingToDo,
  updateIsFetchingToDo,
  updateIsUpdatingToDo,
  updateShouldResetNewTaskForm,
  updateShouldUpdateTextEditorContent,
  updateUpdatingTaskId,
  updateTaskInTodoList,
  updateShowEmbeddedCreateToDoModal,
  updateIsTaskSubmitting,
  updateToDoActiveTabIndex,
  updateNewTask,
  updateNewTodoPlayerTask,
  updateIncomingTasks,
  initFocusPlayer,
  updatePlayerClassSessionId,
  updatePlayerMetaResolved,
  hydratePlayerClassSessionId,
  updateFocusPlayerTotalDuration,
  updatePlayerTasks,
  updateIsSearchingToDos,
  updateTasksPlayedIds,
  updatePlayerSelectedTask,
  updatePlayerTaskCompletion,
  updatePlayerIsReadyToSendUpdates,
  setPlayerIsSyncingFromDesktop,
  updateIsConvertingBrianDump,
  updateShowBrainDumpTasksModal,
  updateBrainDumpModalConvertedTasks,
  updateIsAddingConvertedTasksToPlayer,
  updatePlayerAddTask,
  updatePlayerSearchResults,
  updatePlayerIntention,
  updatePlayerFocusAlignmentScore,
  resetToDoPlayer,
  updatePlayerShowNewTaskModal,
  updateTopScoreDrawerTaskId,
  updateBrainDump,
  updatePlayerCompletedTask,
  syncWithDevicesPlayerTasks,
  updateTodoFilters,
  updateShowNewTaskForm,
  updateSelectedTaskToTaskForm,
  updateIsDeletingTask,
  resetTodoFilters,
  resetTodoPanelFilters,
  updateShowVagueInputPrompt,
  updateIsPausedFromDesktop,
  addReactionToComment,
  removeReactionFromComment,
  removeReactionFromTask,
  updateShowFilters,
  updateWheelSource,
  updatePlayerSelectedTabIndex
} = todo.actions;
export default todo.reducer;
